// src/components/empresas/CertificadosPage/CertificadosPresentation.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Grid,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  VpnKey as VpnKeyIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Componentes compartidos
import { 
  CardContainer, 
  LoadingState, 
  DataTable, 
  EmptyState, 
  FormField, 
  ConfirmDialog 
} from '../../shared';

/**
 * Componente de presentación para la gestión de certificados digitales
 * Solo se encarga de la UI, no contiene lógica de negocio
 */
const CertificadosPresentation = ({
  // Data
  empresa,
  certificados,
  certificadoSeleccionado,
  
  // UI state
  openDialog,
  openDeleteDialog,
  showPassword,
  formData,
  formErrors,
  certificadoFile,
  
  // Loading states
  loading,
  cargandoCertificado,
  eliminandoCertificado,
  
  // Error states
  error,
  errorCargando,
  errorEliminando,
  
  // Handlers
  onNavigateToEmpresas,
  onOpenDialog,
  onCloseDialog,
  onOpenDeleteDialog,
  onCloseDeleteDialog,
  onDeleteCertificado,
  onInputChange,
  onDateChange,
  onFileChange,
  onToggleShowPassword,
  onSubmit,
  
  // Helpers
  getCertificadoStatus,
  isCertificadoExpired,
  isCertificadoExpiringInSoon,

}) => {
  // Error globales para mostrar
  const globalError = error || errorCargando || errorEliminando;
  
  // Columns for DataTable
  const certificadosColumns = [
    {
      field: 'nombre',
      headerName: 'Nombre',
      renderCell: (val, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VpnKeyIcon 
            color={row.activo ? "primary" : "disabled"} 
            sx={{ mr: 1, fontSize: 20 }} 
          />
          {val}
        </Box>
      )
    },
    {
      field: 'fecha_expiracion',
      headerName: 'Fecha Expiración',
      renderCell: (val, row) => (
        <>
          {val ? (
            <>
              {new Date(val).toLocaleDateString()}
              {isCertificadoExpiringInSoon(row) && !isCertificadoExpired(row) && (
                <Tooltip title="El certificado expira pronto">
                  <WarningIcon color="warning" sx={{ ml: 1, fontSize: 16 }} />
                </Tooltip>
              )}
              {isCertificadoExpired(row) && (
                <Tooltip title="El certificado ha expirado">
                  <ErrorIcon color="error" sx={{ ml: 1, fontSize: 16 }} />
                </Tooltip>
              )}
            </>
          ) : (
            "No especificada"
          )}
        </>
      )
    },
    {
      field: 'estado',
      headerName: 'Estado',
      align: 'center',
      renderCell: (_, row) => {
        const status = getCertificadoStatus(row);
        return (
          <Chip 
            label={status.label} 
            color={status.color}
            size="small"
          />
        );
      }
    }
  ];
  
  // Actions for DataTable
  const certificadosActions = [
    {
      icon: <DeleteIcon fontSize="small" />,
      tooltip: 'Eliminar certificado',
      onClick: onOpenDeleteDialog
    }
  ];
  
  // Si no hay empresa y no está cargando, mostrar mensaje
  if (!empresa && !loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Button
            onClick={onNavigateToEmpresas}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2 }}
          >
            Volver a Empresas
          </Button>
          
          <EmptyState
            title="No se ha seleccionado una empresa"
            message="Por favor, seleccione una empresa desde el administrador de empresas."
            actionText="Ir a Empresas"
            onAction={onNavigateToEmpresas}
            usePaper={true}
          />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button 
            onClick={onNavigateToEmpresas} 
            sx={{ textTransform: 'none', fontWeight: 'normal' }}
          >
            Empresas
          </Button>
          {empresa && (
            <Typography color="text.primary">{empresa.razon_social}</Typography>
          )}
          <Typography color="text.primary">Certificados Digitales</Typography>
        </Breadcrumbs>
      </Box>
      
      <CardContainer
        title="Certificados Digitales"
        subheader={empresa ? `Certificados para ${empresa.nombre_corto || empresa.razon_social} (RUT: ${empresa.rut}-${empresa.dv})` : ''}
        actionComponent={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onOpenDialog}
            size="small"
          >
            Nuevo Certificado
          </Button>
        }
      >
        <LoadingState loading={loading} error={globalError}>
          {certificados && certificados.length > 0 ? (
            <DataTable
              columns={certificadosColumns}
              data={certificados}
              actions={certificadosActions}
              pagination={false}
              stickyHeader={false}
              emptyMessage="No hay certificados registrados para esta empresa."
            />
          ) : (
            <EmptyState
              title="No hay certificados registrados"
              message="No hay certificados digitales registrados para esta empresa. Por favor, cargue un nuevo certificado digital (.pfx)."
              actionText="Nuevo Certificado"
              onAction={onOpenDialog}
              icon={<VpnKeyIcon style={{ fontSize: 48, opacity: 0.6 }} />}
              usePaper={true}
            />
          )}
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Información sobre certificados digitales:
            </Typography>
            <Typography variant="body2">
              • Los certificados digitales son utilizados para autenticarse con el SII y consultar el RCV.
            </Typography>
            <Typography variant="body2">
              • Debe ser un certificado válido emitido por el SII para la empresa seleccionada.
            </Typography>
            <Typography variant="body2">
              • El archivo debe estar en formato .pfx y debe conocer la contraseña del certificado.
            </Typography>
            <Typography variant="body2">
              • Puede cargar varios certificados, pero solo los marcados como activos estarán disponibles.
            </Typography>
          </Box>
        </LoadingState>
      </CardContainer>
      
      {/* Formulario para cargar nuevo certificado */}
      <ConfirmDialog
        open={openDialog}
        onClose={onCloseDialog}
        onConfirm={onSubmit}
        title="Cargar Nuevo Certificado Digital"
        confirmText="Cargar Certificado"
        loading={cargandoCertificado}
        content={
          <Box component="form" onSubmit={onSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Archivo de Certificado (.pfx)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 1.5, textTransform: 'none' }}
                  color={formErrors.certificadoFile ? "error" : "primary"}
                >
                  {certificadoFile ? certificadoFile.name : 'Seleccionar archivo de certificado...'}
                  <input
                    type="file"
                    accept=".pfx"
                    hidden
                    onChange={onFileChange}
                  />
                </Button>
                {formErrors.certificadoFile ? (
                  <Typography variant="caption" color="error">
                    {formErrors.certificadoFile}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Seleccione el archivo de certificado digital (.pfx) emitido por el SII
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <FormField
                  name="nombre"
                  label="Nombre del Certificado"
                  placeholder="Ej: Certificado SII Empresa XYZ"
                  fullWidth
                  required
                  value={formData.nombre}
                  onChange={onInputChange}
                  disabled={cargandoCertificado}
                  error={formErrors.nombre}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormField
                  type="password"
                  name="password"
                  label="Contraseña del Certificado"
                  fullWidth
                  required
                  value={formData.password}
                  onChange={onInputChange}
                  disabled={cargandoCertificado}
                  error={formErrors.password}
                  showPassword={showPassword}
                  toggleShowPassword={onToggleShowPassword}
                />
              </Grid>
              
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                  <DatePicker
                    label="Fecha de Expiración (opcional)"
                    value={formData.fecha_expiracion ? dayjs(formData.fecha_expiracion) : null}
                    onChange={onDateChange}
                    disablePast
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: 'Fecha en que expira el certificado (opcional)'
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.activo}
                      onChange={onInputChange}
                      name="activo"
                      color="primary"
                    />
                  }
                  label="Certificado activo"
                />
              </Grid>
            </Grid>
          </Box>
        }
      />
      
      {/* Diálogo de confirmación para eliminar certificado */}
      <ConfirmDialog
        open={openDeleteDialog}
        onClose={onCloseDeleteDialog}
        onConfirm={onDeleteCertificado}
        title="Confirmar Eliminación"
        severity="error"
        confirmText="Eliminar"
        loading={eliminandoCertificado}
        content={
          <Typography>
            ¿Está seguro que desea eliminar el certificado{' '}
            <strong>{certificadoSeleccionado ? certificadoSeleccionado.nombre : ''}</strong>?
            Esta acción no se puede deshacer.
          </Typography>
        }
      />

    </Container>
  );
};

CertificadosPresentation.propTypes = {
  // Data
  empresa: PropTypes.object,
  certificados: PropTypes.array,
  certificadoSeleccionado: PropTypes.object,
  
  // UI state
  openDialog: PropTypes.bool.isRequired,
  openDeleteDialog: PropTypes.bool.isRequired,
  showPassword: PropTypes.bool.isRequired,
  formData: PropTypes.object.isRequired,
  formErrors: PropTypes.object.isRequired,
  certificadoFile: PropTypes.object,
  
  // Loading states
  loading: PropTypes.bool.isRequired,
  cargandoCertificado: PropTypes.bool.isRequired,
  eliminandoCertificado: PropTypes.bool.isRequired,
  
  // Error states
  error: PropTypes.string,
  errorCargando: PropTypes.string,
  errorEliminando: PropTypes.string,
  
  // Handlers
  onNavigateToEmpresas: PropTypes.func.isRequired,
  onOpenDialog: PropTypes.func.isRequired,
  onCloseDialog: PropTypes.func.isRequired,
  onOpenDeleteDialog: PropTypes.func.isRequired,
  onCloseDeleteDialog: PropTypes.func.isRequired,
  onDeleteCertificado: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
  onToggleShowPassword: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  
  // Helpers
  getCertificadoStatus: PropTypes.func.isRequired,
  isCertificadoExpired: PropTypes.func.isRequired,
  isCertificadoExpiringInSoon: PropTypes.func.isRequired,

};

export default CertificadosPresentation;